import React, {useContext, useEffect, useState} from 'react';
import EnhancedTable from '../components/EnhancedTable';
import MyTeamFilter from '../components/MyTeamFilter';
import {getData} from '../utils/apiUtils';
import { getCurrentYear } from '../utils/dateUtils';
import {ErrorContext} from "../utils/constants";
import {loggedInUserId} from '../auth/msalConfig';
import {makeStyles} from "@material-ui/core/styles";
import RefreshIcon from '@material-ui/icons/Refresh';

const upcomingMilestonsHeadCells = [
  { id: 'id', numeric: false, label: 'Milestone Number' },
  { id: 'name', numeric: false, label: 'Milestone Name' },
  { id: 'owner', numeric: false, disablePadding: false, label: 'Milestone Owner' },
  { id: 'initiativeName', numeric: false, label: 'Initiative Name' },
  { id: 'statusName', numeric: false, label: 'Status' },
  { id: 'endDate', numeric: false, label: 'Due Date', format: 'date' },
];

const currentInitiativeHeadCells = [
  { id: 'id', numeric: false, disablePadding: false, label: 'Initiative Number' },
  { id: 'initiative_name', numeric: false, disablePadding: false, label: 'Initiative Name' },
  { id: 'owner', numeric: false, disablePadding: false, label: 'Initiative Owner' },
  { id: 'status', numeric: false, disablePadding: true, label: 'Status' },
  { id: 'max_date', numeric: false, disablePadding: true, label: 'Due Date', format: 'date' },
  { id: 'netCash', numeric: false, disablePadding: true, label: `Net Cash in ${getCurrentYear()}`, format: 'currency' },
];

const useStyles = makeStyles(() => ({
  filterBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    width: '100%'
  },
  refreshBox: {
    display: 'flex',
    alignItems: 'center'
  },
  cursorPointer: {
    cursor: 'pointer'
  }
}));

export default function MyTeam({current}) {
  const { triggerError } = useContext(ErrorContext);
  const [ allInitiatives, setAllInitiatives ] = useState([]);
  const [ currentInitiatives, setCurrentInitiatives ] = useState([]);
  const [ allMilestones, setAllMilestones ] = useState([]);
  const [ upcomingMilestones, setUpcomingMilestones ] = useState([]);
  const [ team, setTeam ] = useState({team: []});
  const [ currentOid, setCurrentOid ] = useState(loggedInUserId());
  const [ filterOidList, setFilterOidList] = useState([]);

  const classes = useStyles();

  const onError = () => triggerError({
    title: 'Communication Error',
    description: 'We were unable to read your team milestone and initiative data from the server. Please try again.',
    onRetry: () => window.location.reload(),
  });

  const updateInitiatives = (data) => {
    setCurrentInitiatives(data);
    setAllInitiatives(data);
  };

  const updateMilestones = (data) => {
    setAllMilestones(data);
    setUpcomingMilestones(data);
  };

  useEffect(() => {
    document.title = `Rise by TransAlta My ${current ? 'Current': 'Past'} Team Initiatives Dashboard`;
  }, []);

  useEffect(() => {
    if (!currentOid) {
      return;
    }
    getData(`/dashboards/teamcurrentinitiatives/${currentOid}?current=${current}` , updateInitiatives, onError);
    getData(`/dashboards/teamupcomingmilestones/${currentOid}?current=${current}` , updateMilestones, onError);
    getData(`/users/forteam/${currentOid}` , setTeam, onError);
  }, [current, currentOid]);

  const filterChange = (oid, add) => {
    let newFilterList = Object.assign([], filterOidList);
    if (add) {
      newFilterList.push(oid);
    } else {
      newFilterList = newFilterList.filter(item => item !== oid);
    }
    setFilterOidList(newFilterList);


    let initiativeList = allInitiatives;
    let milestoneList = allMilestones;
    if (newFilterList.length > 0 ) {
      initiativeList = allInitiatives.filter(initiative => {
        for (let index = 0; index < newFilterList.length; index++) {
          const oid = newFilterList[index];
          if (initiative.oids.includes(oid)) {
            return true;
          }
        }
        return false;
      });

      milestoneList = allMilestones.filter(milestone => {
        for (let index = 0; index < newFilterList.length; index++) {
          const oid = newFilterList[index];
          if (milestone.oid === oid) {
            return true;
          }
        }
        return false;
      });
    }

    setCurrentInitiatives(initiativeList);
    setUpcomingMilestones(milestoneList);
  };

  const changeTeam = (oid, isManager) => {
    if (!isManager) {
      setCurrentOid(oid);
      return;
    }
    getData(`/users/teambymanager/${oid}` , (data) => {
      setTeam(data);
      if (data.team.length > 0) {
        setCurrentOid(data.team[0].oid);
      } else {
        setCurrentOid(null);
        updateInitiatives([]);
        updateMilestones([]);
      }
    }, onError);
  };

  const resetFilter = () => {
    setCurrentOid(loggedInUserId());
  };

  const getFilterByTeamBar = () => {
    return (
      <div className={classes.filterBox}>
       <h1>Filter By Team Member</h1>
        <div className={classes.refreshBox}>
          <RefreshIcon className={classes.cursorPointer} onClick={resetFilter}/>
        </div>
      </div>
    );
  };

  if (current) {
    return (
      <main>
        {getFilterByTeamBar()}
        <MyTeamFilter team={team} filterChange={filterChange} changeTeam={changeTeam}/>
        <h2>Upcoming Initiative Milestones</h2>
        Here are the milestones due this month.
        <EnhancedTable id='5817e808-1c6b-4f2a-9f18-712fa3b8da5b'
                       headCells={upcomingMilestonsHeadCells}
                       rows={upcomingMilestones}
                       allowExport={true}
                       exportFilename='Upcoming Initiative Milestones'
                       clickData={{url: '/ui/initiativemilestones/', key: 'initiative_id'}}/>
        <h2>Current Initiatives</h2>
        Here is a list of current initiatives.
        <EnhancedTable id='2c991d62-ad97-40c9-9524-d07542c31563'
                       headCells={currentInitiativeHeadCells}
                       rows={currentInitiatives}
                       allowExport={true}
                       exportFilename='Current Initiatives'
                       clickData={{url: '/ui/initiativecard/', key: 'id'}}/>
      </main>
    );
  }

  return (
    <main>
      {getFilterByTeamBar()}
      <MyTeamFilter team={team} filterChange={filterChange} changeTeam={changeTeam}/>
      <h2>All Initiative Milestones</h2>
      Here are the all team milestones.
      <EnhancedTable id='1ef0ee0c-f4e6-4480-9fb3-9ef64c32d653'
                     headCells={upcomingMilestonsHeadCells}
                     allowExport={true}
                     exportFilename='Upcoming Initiative Milestones'
                     rows={upcomingMilestones}
                     clickData={{url: '/ui/initiativemilestones/', key: 'initiative_id'}}/>
      <h2>Past Initiatives</h2>
      Here is a list of past initiatives.
      <EnhancedTable id='5de7130d-99a0-4763-a1a9-ae983d23de1f'
                     headCells={currentInitiativeHeadCells}
                     allowExport={true}
                     exportFilename='Current Initiatives'
                     rows={currentInitiatives}
                     clickData={{url: '/ui/initiativecard/', key: 'id'}}/>
    </main>
  );
}

